import { ESignalQuality } from '../infra/access-point-edge-hub.service';

/**
 * Relatório de saúde de conexão de um edge — `GET /EdgeEventLog/ConnectionReport`
 * do AccessEdgeManager.
 *
 * O contrato deste serviço é camelCase (`edgeId`, `days`, `qualityThresholdsMs`),
 * ao contrário do Actuar.Pack, que serializa PascalCase. Os tipos abaixo são 1:1
 * com o fio, de propósito: não existe mapeador de casing aqui.
 *
 * Fusos, e onde o `DateInterceptor` mexe:
 * - `window.fromUtc` / `window.toUtc` terminam em `Utc`, portanto o interceptor
 *   converte-os na resposta e eles chegam ao componente já em hora de parede da
 *   conta, numa string SEM offset. Voltar a lê-los com `{ zone: 'utc' }` desloca
 *   o valor duas vezes.
 * - `days[].date`, `dropsAt[]` e `rttSamples[].t` passam intactos: o servidor já
 *   os devolve no fuso pedido (os dois primeiros) ou em epoch UTC (o terceiro).
 */
export interface EdgeConnectionReport {
  edgeId: string;
  /** Fuso IANA em que o servidor montou as grades, o `summary` e o `dropsAt`. */
  timeZone: string;
  qualityThresholdsMs: EdgeConnectionQualityThresholdsMs;
  window: EdgeConnectionReportWindow;
  /** `null` quando o pedido não trouxe `from`/`to` — janela viva do stream. */
  period: EdgeConnectionReportPeriod | null;
  days: EdgeConnectionReportDay[];
  totals: EdgeConnectionReportTotals;
  insights: EdgeConnectionInsight[];
  summary: EdgeConnectionReportSummary;
  /** Instantes das quedas, em hora LOCAL (`YYYY-MM-DDTHH:mm:ss`, sem offset). */
  dropsAt: string[];
  rttSamples: EdgeConnectionRttSample[];
}

/**
 * Limiares reais de qualidade do servidor, em milissegundos. As réguas
 * tracejadas do gráfico e o texto do rodapé saem daqui — nunca de constantes no
 * front, para a tela acompanhar uma recalibração do servidor.
 */
export interface EdgeConnectionQualityThresholdsMs {
  excellent: number;
  good: number;
  fair: number;
  poor: number;
}

export interface EdgeConnectionReportWindow {
  /** Ver a nota de fuso em {@link EdgeConnectionReport}: chega já local. */
  fromUtc: string;
  toUtc: string;
  /** `true` = o histórico bateu o teto de eventos e a janela é o que sobrou. */
  truncated: boolean;
}

/**
 * Período pedido, e o aviso de que ele não foi servido por inteiro.
 *
 * O relatório costura duas fontes: a janela viva do stream Redis e o histórico
 * durável do directory (espec 003). Quando o directory não responde, os dias
 * antigos ficam de fora e o servidor **anuncia** em vez de calar — o relatório
 * degrada para a janela viva e a tela tem de dizê-lo.
 */
export interface EdgeConnectionReportPeriod {
  /** `yyyy-MM-dd`, dia LOCAL do inquilino. */
  from: string;
  to: string;
  /** `true` = dado parcial anunciado; os dias antigos não entraram. */
  historyUnavailable: boolean;
}

/** Um dia da grade: 24 posições por métrica, índice = hora local (0–23). */
export interface EdgeConnectionReportDay {
  /** `YYYY-MM-DD` na hora local pedida. */
  date: string;
  /** Convenção JS: 0 = domingo, 6 = sábado. */
  weekday: number;
  /**
   * Dia servido pelo rollup horário do directory, não pelo stream cru.
   *
   * As contagens são idênticas em semântica, mas **não há `rttSamples`** para
   * estes dias — o cru já não existe. Portanto os dois gráficos de latência só
   * cobrem a janela viva, e a tela tem de dizer isso em vez de deixar parecer
   * que o período inteiro foi medido.
   */
  aggregated: boolean;
  /** Transições para `Fair` ou `Poor` — "a conexão piorou". */
  degradations: number[];
  /** Subconjunto grave de `degradations`. */
  poor: number[];
  passages: number[];
  /** Conexão perdida por completo — fenómeno separado da lentidão. */
  drops: number[];
}

export interface EdgeConnectionReportTotals {
  degradations: number;
  poor: number;
  passages: number;
  drops: number;
}

/**
 * Frases prontas do diagnóstico, geradas por regra no servidor. Renderizar como
 * texto corrido: o front não redige conclusões próprias nem traduz estas
 * frases. A lista pode vir vazia quando o dado não sustenta nenhuma.
 */
export interface EdgeConnectionInsight {
  key: EdgeConnectionInsightKey;
  text: string;
}

/** Chave estável para estilização — o texto é do servidor. */
export type EdgeConnectionInsightKey =
  | 'occupancy-scaling'
  | 'occupancy-flat'
  | 'weekend-contrast'
  | 'drops-not-latency'
  | 'drops-after-latency';

export interface EdgeConnectionReportSummary {
  weekdays: EdgeConnectionSummarySlice;
  weekend: EdgeConnectionSummarySlice;
  occupancyBands: EdgeConnectionOccupancyBand[];
}

export interface EdgeConnectionSummarySlice {
  degradations: number;
  drops: number;
}

/**
 * Horas com a casa aberta, agrupadas por lotação (pessoas nos 90 minutos
 * anteriores). É o veredito do diagnóstico: se `ratePerHour` sobe com a faixa,
 * o problema acompanha a quantidade de gente na rede.
 */
export interface EdgeConnectionOccupancyBand {
  occupancyMin: number;
  /** `null` na última faixa — "45 ou mais". */
  occupancyMax: number | null;
  hours: number;
  degradations: number;
  ratePerHour: number;
}

/**
 * Latência de uma amostra de heartbeat. Amostragem enviesada por desenho: o RTT
 * só é gravado quando a qualidade MUDA, portanto período sem pontos é período
 * estável, não ausência de tráfego.
 */
export interface EdgeConnectionRttSample {
  /** Epoch em milissegundos, UTC. */
  t: number;
  rttMs: number;
  /** Nome do enum de qualidade, tal como o servidor o serializa. */
  quality: keyof typeof ESignalQuality;
}

export interface EdgeConnectionReportState {
  report: EdgeConnectionReport | null;
  /**
   * Nome do ponto de acesso ligado a este edge, para a tela o chamar como o
   * utilizador o chama. `null` quando o edge ainda não tem ponto configurado —
   * estado normal, não erro — ou quando a leitura do directory falhou, que não
   * pode derrubar o diagnóstico.
   */
  accessPointName: string | null;
}

export const initialEdgeConnectionReportState: EdgeConnectionReportState = {
  report: null,
  accessPointName: null,
};

/** Métricas de `days[]` que a grade dia×hora sabe desenhar. */
export type EdgeConnectionGridMetric = 'degradations' | 'poor' | 'passages';

/**
 * Uma hora da grade. `level` é a intensidade da rampa de cor (0 = vazio,
 * 1–5 = do mais claro ao mais escuro); a regra que o produz está em
 * `edge-connection-report.store.ts`.
 */
export interface EdgeConnectionGridCell {
  hour: number;
  value: number;
  level: number;
  /** Houve queda de conexão nesta hora — desenhado só na grade de degradações. */
  hasDrop: boolean;
}

export interface EdgeConnectionGridRow {
  /** `YYYY-MM-DD` local, tal como veio de `days[].date`. */
  date: string;
  weekday: number;
  isWeekend: boolean;
  /**
   * Primeiro e último dia da janela são quase sempre parciais — ela começa e
   * acaba a meio do dia. O total do dia fica marcado; taxas por hora não sofrem.
   */
  isPartial: boolean;
  /**
   * Dia servido pelo rollup horário do directory. As contagens valem; o que não
   * existe é a medição de cada amostra, portanto este dia não aparece nos
   * gráficos de latência.
   */
  isAggregated: boolean;
  cells: EdgeConnectionGridCell[];
  total: number;
}

/** Grade dia×hora já pronta para desenhar, incluindo a linha e a coluna de totais. */
export interface EdgeConnectionGrid {
  rows: EdgeConnectionGridRow[];
  /** 24 posições — a linha de rodapé da tabela. */
  hourTotals: number[];
  total: number;
  /** Maior valor de uma célula: é dele que sai a rampa de cor. */
  max: number;
}

/**
 * Nível de latência numa hora do dia, reconstruído sobre a janela inteira.
 *
 * **Não há lacuna, e isso não é preenchimento inventado.** `rttSamples` é um
 * fluxo de MUDANÇAS de estado, não de medições periódicas — a própria spec diz
 * que "períodos sem pontos são períodos estáveis". Estável quer dizer que o
 * nível medido por último se manteve, portanto o nível de qualquer hora é
 * conhecido: ou foi medido nela, ou é o que vinha de antes.
 *
 * `carriedHours` conta quantas das horas agregadas não tiveram medição própria
 * e herdaram o nível. Sem isso, uma hora reconstruída a partir de uma medição de
 * três horas antes leria com a mesma confiança de uma medida no minuto.
 *
 * `typicalMs` é a mediana dos níveis daquela hora ao longo dos dias; `peakMs` é
 * o pior valor que ela registrou em qualquer dia. A distância entre os dois é o
 * sinal: hora larga é hora que oscila, hora estreita é consistente.
 */
export interface EdgeConnectionHourlyLatency {
  /** Hora local, 0–23. */
  hour: number;
  typicalMs: number | null;
  peakMs: number | null;
  /** Amostras que caíram mesmo dentro desta hora, somando todos os dias. */
  samples: number;
  /** Horas agregadas que herdaram o nível em vez de o medirem. */
  carriedHours: number;
}

/**
 * O que a tela pede: um edge e, opcionalmente, um período.
 *
 * Sem `period` o servidor devolve a janela viva do stream — que na medição de
 * produção da espec 003 tem mediana de ~1 semana, mas menos de um dia nos edges
 * mais falantes. Com `period`, os dias além do stream vêm do histórico durável.
 */
export interface EdgeConnectionReportQuery {
  edgeId: string;
  period?: { from: string; to: string };
}
